import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  LinearProgress,
  Typography
} from "@mui/material";

import { getCorrespondences } from "../../services/backend/CorrespondenceService";
import { fetchRequestsFiledPage } from "../../services/backend/FiledSearchService";
import FiledItem from "./FiledItem";
import FiledItemFilter from "./FiledItemFilter";
import useFiledSearch, { filedLoadErrorMessage } from "./useFiledSearch";
import { setCorrespondence } from "../../redux/slices/correspondenceSlice";
import { PERMISSIONS } from "../../constants";

export default function FiledRequestsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const correspondences = useSelector((state) => state.correspondences.value);
  const [correspondenceSelected, setCorrespondenceSelected] = useState("all");

  // Bound to the current user: requestsFiled is readable only through the
  // participants array, so there is no unscoped variant of this list.
  const fetchPage = useCallback(
    (args) => fetchRequestsFiledPage({ userId: user?.userId, ...args }),
    [user?.userId]
  );

  const {
    items,
    search,
    setSearch,
    isSearching,
    range,
    applyRange,
    loading,
    loadingMore,
    cascading,
    hasMore,
    loadError,
    statusMessage,
    sentinelRef,
    refresh,
  } = useFiledSearch({
    fetchPage,
    filedType: correspondenceSelected,
    filterTypeOnServer: false,
    enabled: !!user?.userId,
  });

  const handleCorrespondences = async () => {
    if (correspondences) return;
    const responseJSON = await getCorrespondences(user.token);
    dispatch(setCorrespondence(responseJSON));
  };

  useEffect(() => {
    if (!user.permissions.includes(PERMISSIONS.REQUESTSFILED.LIST)) return navigate(`/home`);
    handleCorrespondences();
  }, []); // eslint-disable-line

  return (
    <Container maxWidth={false}>
      <FiledItemFilter
        handleFilter={applyRange}
        search={search}
        onSearchChange={setSearch}
        activeRange={range.key}
        correspondences={correspondences}
        filedType={correspondenceSelected}
        onFiledTypeChange={setCorrespondenceSelected}
        onRefresh={refresh}
        busy={loading || loadingMore || cascading}
        statusMessage={statusMessage}
      />
      {loadError && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
          {filedLoadErrorMessage(loadError)}
        </Alert>
      )}
      {(cascading || loadingMore) && <LinearProgress color="primary" sx={{ height: 4 }} />}
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
        {loading ? (
          <CircularProgress sx={{ margin: 'auto', my: 4 }} />
        ) : items.length > 0 ? (
          items.map((item) => (
            <FiledItem key={item.filedId} item={item} match={item.match} />
          ))
        ) : (
          <Typography sx={{ m: 4 }} color="text.secondary">
            {isSearching
              ? `No hay radicados que coincidan con «${search}» en este periodo.`
              : "No hay ningún radicado con el filtro seleccionado"}
          </Typography>
        )}
      </Box>
      {hasMore && !loading && <Box ref={sentinelRef} sx={{ width: "100%", py: 2 }} />}
    </Container>
  );
}
